import React, { useEffect, useRef, useState } from 'react';

import { ItemDeNotaAIdentificar, ItemDoPedido } from '../../../interfaces/PrevisaoPecas';
import { formatarDiaPuro, formatarQuantidade, hojeIso } from '../formatos';
import BarraDeChegada from './BarraDeChegada';
import '../../../components/modal/add-car/AddCarModal.css'; // overlay compartilhado
import '../../../shared/ui/dashboard-base.css'; // casco `do-modal-*`
// Campos, rótulos, hints e botões do vocabulário de configuração. Import explícito: contar
// com o CSS que a tela hospedeira carrega é acoplamento invisível (lição do `BUG-V74`).
import '../../admin-sistema/pages/css/adminSistema.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface Props {
  item: ItemDoPedido;
  pedidoNumero: string | null;
  /** Quando presente, o registro IDENTIFICA este item da nota (que não casou sozinho). */
  itemDaNota?: ItemDeNotaAIdentificar | null;
  salvando: boolean;
  onConfirmar: (dados: { quantidade: number; chegouEm: string }) => void;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

/**
 * Registro de chegada feito por uma pessoa (previsao-pecas/at-02) — movido da antiga tela de
 * Estoque. Dois usos:
 *
 * - **Peça que chegou antes da nota entrar no Nasajon** (o ERP atrasa 1 a 2 dias).
 * - **Identificar um item da nota** que não casou com nenhum item do pedido (produto trocado pelo
 *   fornecedor, por exemplo). Aí a quantidade e o dia já vêm da nota.
 *
 * ⚠️ Acima do saldo é PERMITIDO e fica marcado como excedente: peça que chegou não some. A hint diz
 * isso antes do clique.
 */
const RegistrarChegadaModal: React.FC<Props> = ({
  item,
  pedidoNumero,
  itemDaNota,
  salvando,
  onConfirmar,
  onFechar,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const hoje = hojeIso();
  const unidade = item.unidade ?? 'un';

  const [quantidade, setQuantidade] = useState<string>(
    String(itemDaNota?.quantidade ?? (item.saldo > 0 ? item.saldo : '')),
  );
  const [chegouEm, setChegouEm] = useState<string>(hoje);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  const numero = Number(String(quantidade).replace(',', '.'));
  const acimaDoSaldo = Number.isFinite(numero) && numero > item.saldo;

  const confirmar = () => {
    if (!Number.isFinite(numero) || numero <= 0) {
      setErro('Informe uma quantidade maior que zero.');
      return;
    }
    if (!chegouEm) {
      setErro('Informe o dia em que a peça chegou.');
      return;
    }
    if (chegouEm > hoje) {
      setErro('O dia da chegada não pode ser no futuro.');
      return;
    }
    setErro(null);
    onConfirmar({ quantidade: numero, chegouEm });
  };

  const titulo = itemDaNota ? 'Identificar item da nota' : 'Registrar chegada';

  return (
    <div className="modal-overlay">
      <div className="do-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="do-modal-header">
          <h2 className="do-modal-titulo">{titulo}</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="do-modal-corpo">
          <p className="do-modal-apoio">
            <strong>{item.produtoDescricao ?? 'Peça sem descrição'}</strong>
            {item.produtoCodigo ? ` (${item.produtoCodigo})` : ''} — pedido {pedidoNumero ?? 'sem número'}. Previsão:{' '}
            {formatarDiaPuro(item.previsao)}.
          </p>

          {itemDaNota && (
            <p className="admin-hint" style={{ marginBottom: 16 }}>
              A nota {itemDaNota.notaNumero ?? 'sem número'} trouxe{' '}
              <strong>
                {formatarQuantidade(itemDaNota.quantidade ?? 0)} {itemDaNota.unidade ?? ''}{' '}
                {itemDaNota.descricao ?? 'item sem descrição'}
              </strong>
              {itemDaNota.codigo ? ` (código ${itemDaNota.codigo})` : ''}, que o sistema não conseguiu ligar a um item do
              pedido. Confirmando, esta quantidade passa a contar como chegada desta peça.
            </p>
          )}

          <div style={{ marginBottom: 16 }}>
            <BarraDeChegada recebida={item.chegada} pedida={item.pedida} unidade={unidade} largura={90} />
          </div>

          <div className="admin-input-group" style={{ marginBottom: 16 }}>
            <label className="admin-label" htmlFor="chegada-quantidade">
              Quantidade que chegou, em {unidade}
            </label>
            <input
              id="chegada-quantidade"
              className="admin-input"
              type="number"
              min="0"
              step="any"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
            />
            <p className="admin-hint">
              {acimaDoSaldo
                ? `Acima do que falta chegar (${formatarQuantidade(item.saldo)} ${unidade}). A diferença fica registrada ` +
                  'como excedente — não some, e aparece no pedido.'
                : 'Chegou menos? Informe só o que chegou — o restante continua pendente.'}
            </p>
          </div>

          <div className="admin-input-group" style={{ marginBottom: 16 }}>
            <label className="admin-label" htmlFor="chegada-data">
              Dia da chegada
            </label>
            <input
              id="chegada-data"
              className="admin-input"
              type="date"
              max={hoje}
              value={chegouEm}
              onChange={(e) => setChegouEm(e.target.value)}
            />
            <p className="admin-hint">O dia em que a peça chegou — não o de hoje, se você estiver registrando depois.</p>
          </div>

          {erro && (
            <p className="erro-de-tela" role="alert">
              {erro}
            </p>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={onFechar} disabled={salvando}>
              Cancelar
            </button>
            <button type="button" className="admin-btn admin-btn-primary" onClick={confirmar} disabled={salvando}>
              {salvando ? 'Registrando...' : titulo}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarChegadaModal;
